import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Snackbar, Alert } from '@mui/material';
import type { ApiResponse } from '@/types';

interface ErrorNotification {
  id: string;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
  autoHideDuration?: number;
}

interface ErrorContextValue {
  showError: (message: string, autoHideDuration?: number) => void;
  showWarning: (message: string, autoHideDuration?: number) => void;
  showSuccess: (message: string, autoHideDuration?: number) => void;
  showInfo: (message: string, autoHideDuration?: number) => void;
  showApiError: (error: ApiResponse<never>['error'], autoHideDuration?: number) => void;
  clearError: (id: string) => void;
  clearAllErrors: () => void;
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({
  children,
  maxNotifications = 3,
}) => {
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);

  const addNotification = useCallback((
    message: string,
    severity: ErrorNotification['severity'],
    autoHideDuration = 6000
  ) => {
    const id = `${Date.now()}-${Math.random()}`;
    const notification: ErrorNotification = {
      id,
      message,
      severity,
      autoHideDuration,
    };

    setNotifications(prev => {
      const newNotifications = [notification, ...prev];
      // Keep only the most recent notifications
      return newNotifications.slice(0, maxNotifications);
    });

    // Auto-remove notification after duration
    if (autoHideDuration > 0) {
      setTimeout(() => {
        clearError(id);
      }, autoHideDuration);
    }
  }, [maxNotifications]);

  const showError = useCallback((message: string, autoHideDuration?: number) => {
    addNotification(message, 'error', autoHideDuration);
  }, [addNotification]);

  const showWarning = useCallback((message: string, autoHideDuration?: number) => {
    addNotification(message, 'warning', autoHideDuration);
  }, [addNotification]);

  const showSuccess = useCallback((message: string, autoHideDuration?: number) => {
    addNotification(message, 'success', autoHideDuration);
  }, [addNotification]);

  const showInfo = useCallback((message: string, autoHideDuration?: number) => {
    addNotification(message, 'info', autoHideDuration);
  }, [addNotification]);

  const showApiError = useCallback((
    error: ApiResponse<never>['error'],
    autoHideDuration?: number
  ) => {
    const message = error?.message || 'An unexpected error occurred';
    addNotification(message, 'error', autoHideDuration);
  }, [addNotification]);

  const clearError = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllErrors = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: ErrorContextValue = {
    showError,
    showWarning,
    showSuccess,
    showInfo,
    showApiError,
    clearError,
    clearAllErrors,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      
      {/* Render notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            mt: index * 7, // Stack notifications vertically
          }}
        >
          <Alert
            severity={notification.severity}
            onClose={() => clearError(notification.id)}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextValue => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};